/**
 * A manager's career in one document: record, titles, streaks, luck, and the
 * opponents they own or fear. Everything is computed from `LeagueHistory`
 * through the same ledger the matchup report uses, so a number on a profile
 * agrees with a number in a report.
 */

import type { LeagueHistory } from "../history";
import type { PersonGame, Record3 } from "../reportBuilder/ledger";
import { buildLedger, countsTowardHistory, MIN_MEETINGS_FOR_RIVAL, recordOf, recordsByOpponent } from "../reportBuilder/ledger";
import { average, round2, winPct } from "../util/math";
import type { GameRecord } from "./gameRecords";
import { toGameRecord, topBy } from "./gameRecords";
import { buildLuck, weeksInProgress } from "./luck";
import type { LuckRow } from "./luck";
import type { ManagerSeasonLine } from "./seasonLines";
import { buildSeasonLines, compareSeasonLines } from "./seasonLines";
import type { Streak, StreakSummary } from "./streaks";
import { summarizeStreaks } from "./streaks";


export interface RivalSummary {
  opponentId: string;
  /** From the profile owner's side. */
  record: Record3;
}

export interface ManagerProfile {
  personId: string;
  name: string;
  /** Has a team in the most recent season. */
  active: boolean;
  /** Their team logo from the latest season Yahoo gave one for; null if never. */
  logoUrl: string | null;
  /** Oldest first. */
  seasons: ManagerSeasonLine[];
  /** Seasons won, oldest first. */
  titles: number[];
  playoffAppearances: number;
  regularSeason: Record3;
  playoffs: Record3;
  /** Regular-season points per game; 0 before any games. */
  pointsPerGame: number;
  highScore: GameRecord | null;
  lowScore: GameRecord | null;
  /** Among finished seasons only; null until one has been played. */
  bestSeason: ManagerSeasonLine | null;
  worstSeason: ManagerSeasonLine | null;
  /**
   * Actual regular-season win rate against the rate their scores should have
   * earned (each week's share of the league they beat, see `buildLuck`).
   * Positive `luck` means the schedule has been kind. Null with no
   * regular-season games.
   */
  luck: { winPct: number; expectedWinPct: number; luck: number } | null;
  longestWinStreak: Streak | null;
  longestLossStreak: Streak | null;
  currentStreak: StreakSummary["current"];
  /** The opponent they've done worst against, if any (a losing record over enough meetings). */
  nemesis: RivalSummary | null;
  /** The opponent they've done best against, if any (a winning record over enough meetings). */
  favoriteVictim: RivalSummary | null;
  /** Adds/drops and trades summed over the seasons Yahoo reported them for. */
  transactions: { moves: number; trades: number; seasonsTracked: number };
}

/** Null when `personId` isn't in the history. */
export function buildManagerProfile(history: LeagueHistory, personId: string): ManagerProfile | null {
  return buildManagerProfiles(history).find((p) => p.personId === personId) ?? null;
}

/** One profile per person, in `history.people` order, sharing one ledger pass. */
export function buildManagerProfiles(history: LeagueHistory): ManagerProfile[] {
  const ledger = buildLedger(history);
  const lines = buildSeasonLines(history, ledger);
  const luckRows = new Map(buildLuck(ledger, "all", weeksInProgress(history)).rows.map((row) => [row.personId, row]));
  const latestTeamKeys = new Set(history.seasons.at(-1)?.teams.map((t) => t.teamKey) ?? []);
  const activeIds = new Set([...latestTeamKeys].map((k) => history.personByTeamKey[k]));
  const logos = latestLogos(history);

  return history.people.map((person) => {
    const games = ledger.filter((g) => g.personId === person.id && countsTowardHistory(g));
    const regular = games.filter((g) => g.gameType === "regular");
    const seasons = lines.filter((l) => l.personId === person.id);
    const finished = seasons.filter((l) => l.complete).sort(compareSeasonLines);
    const streaks = summarizeStreaks(games);
    const regularRecord = recordOf(regular);

    return {
      personId: person.id,
      name: person.name,
      active: activeIds.has(person.id),
      logoUrl: logos.get(person.id) ?? null,
      seasons,
      titles: seasons.filter((l) => l.champion).map((l) => l.season),
      playoffAppearances: seasons.filter((l) => l.madePlayoffs).length,
      regularSeason: regularRecord,
      playoffs: recordOf(games.filter((g) => g.gameType === "playoffs")),
      pointsPerGame: round2(average(regular.map((g) => g.points))),
      highScore: bestOf(games, (g) => g.points),
      lowScore: bestOf(games, (g) => -g.points),
      bestSeason: finished[0] ?? null,
      worstSeason: finished.at(-1) ?? null,
      luck: luckOf(luckRows.get(person.id)),
      longestWinStreak: streaks.longestWin,
      longestLossStreak: streaks.longestLoss,
      currentStreak: streaks.current,
      ...rivalsOf(games),
      transactions: transactionsOf(history, person.id),
    };
  });
}

/** Each person's most recent team logo, so a manager keeps one even after years away. */
function latestLogos(history: LeagueHistory): Map<string, string> {
  const logos = new Map<string, string>();
  for (const season of history.seasons) {
    for (const team of season.teams) {
      const personId = history.personByTeamKey[team.teamKey];
      if (personId && team.logoUrl) logos.set(personId, team.logoUrl); // seasons run oldest first, so later ones win
    }
  }
  return logos;
}

const bestOf = (games: readonly PersonGame[], score: (g: PersonGame) => number): GameRecord | null => {
  const top = topBy(games, score, 1)[0];
  return top ? toGameRecord(top) : null;
};

/** The all-time luck row as win rates: how much better (or worse) their record is than their scores earned. */
function luckOf(row: LuckRow | undefined): ManagerProfile["luck"] {
  if (!row || row.games === 0) return null;
  const wins = row.actual.wins + row.actual.ties / 2;
  return {
    winPct: round2(wins / row.games),
    expectedWinPct: round2(row.expectedWins / row.games),
    luck: round2((wins - row.expectedWins) / row.games),
  };
}

/** Ties on win percentage go to the opponent met more often, then the one met first, so the pick is stable. */
function rivalsOf(games: readonly PersonGame[]): Pick<ManagerProfile, "nemesis" | "favoriteVictim"> {
  const rivals: (RivalSummary & { games: number })[] = recordsByOpponent(games)
    .filter((r) => r.games >= MIN_MEETINGS_FOR_RIVAL)
    .map(({ opponentId, games: meetings, ...record }) => ({ opponentId, record, games: meetings }));

  const pick = (direction: 1 | -1, qualifies: (pct: number) => boolean): RivalSummary | null => {
    const best = [...rivals]
      .filter((r) => qualifies(winPct(r.record)))
      .sort((a, b) => direction * (winPct(b.record) - winPct(a.record)) || b.games - a.games)[0];
    return best ? { opponentId: best.opponentId, record: best.record } : null;
  };
  return {
    // Lowest win percentage first for the nemesis, highest first for the victim.
    nemesis: pick(-1, (pct) => pct < 0.5),
    favoriteVictim: pick(1, (pct) => pct > 0.5),
  };
}

function transactionsOf(history: LeagueHistory, personId: string): ManagerProfile["transactions"] {
  let moves = 0;
  let trades = 0;
  let seasonsTracked = 0;
  for (const season of history.seasons) {
    for (const team of season.teams) {
      if (history.personByTeamKey[team.teamKey] !== personId) continue;
      if (team.moves == null && team.trades == null) continue;
      moves += team.moves ?? 0;
      trades += team.trades ?? 0;
      seasonsTracked++;
    }
  }
  return { moves, trades, seasonsTracked };
}
